import { __rest } from "tslib";
import React from 'react';
import { useFocusWithin, useForkRef } from '@gravity-ui/uikit';
import { useDateFieldProps } from '../../DateField';
import { getButtonSizeForInput } from '../../utils/getButtonSizeForInput';
import { mergeProps } from '../../utils/mergeProps';
import { i18n } from '../i18n';
import { getDateTimeValue } from '../utils';
export function useDatePickerProps(state, _a) {
    var { onFocus, onBlur } = _a, props = __rest(_a, ["onFocus", "onBlur"]);
    const [isActive, setActive] = React.useState(false);
    const { focusWithinProps } = useFocusWithin({
        onFocusWithin: onFocus,
        onBlurWithin: onBlur,
        onFocusWithinChange(isFocusWithin) {
            setActive(isFocusWithin);
            if (!isFocusWithin) {
                state.setOpen(false);
            }
        },
    });
    const { inputProps } = useDateFieldProps(state.dateFieldState, props);
    const inputRef = React.useRef(null);
    const handleRef = useForkRef(inputRef, inputProps.controlRef);
    const calendarRef = React.useRef(null);
    const calendarButtonRef = React.useRef(null);
    const groupRef = React.useRef(null);
    function focusInput() {
        setTimeout(() => {
            var _a;
            (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        });
    }
    const onlyTime = state.hasTime && !state.hasDate;
    return {
        groupProps: Object.assign(Object.assign({ ref: groupRef, tabIndex: -1, role: 'group' }, focusWithinProps), { style: props.style, 'aria-disabled': state.disabled || undefined, onKeyDown: (e) => {
                if (!onlyTime && e.altKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                    e.preventDefault();
                    e.stopPropagation();
                    state.setOpen(true);
                }
            } }),
        fieldProps: mergeProps(inputProps, state.dateFieldState.isEmpty && !isActive && props.placeholder
            ? { value: '' }
            : undefined, { controlRef: handleRef }),
        calendarButtonProps: {
            ref: calendarButtonRef,
            size: getButtonSizeForInput(props.size),
            disabled: state.disabled,
            extraProps: {
                'aria-label': i18n('Calendar'),
                'aria-haspopup': 'dialog',
                'aria-expanded': state.isOpen,
            },
            view: 'flat-secondary',
            onClick: () => {
                setActive(true);
                state.setOpen(!state.isOpen);
            },
        },
        popupProps: {
            open: state.isOpen,
            onEscapeKeyDown: () => {
                state.setOpen(false);
                focusInput();
            },
            onOutsideClick: (e) => {
                var _a;
                if (e.target !== calendarButtonRef.current) {
                    state.setOpen(false);
                }
                if (e.target && ((_a = groupRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))) {
                    focusInput();
                }
            },
            focusTrap: true,
        },
        calendarProps: {
            ref: calendarRef,
            autoFocus: true,
            size: props.size === 's' ? 'm' : props.size,
            disabled: props.disabled,
            readOnly: props.readOnly,
            onUpdate: (d) => {
                state.setDateValue(d);
                if (!state.hasTime) {
                    focusInput();
                }
            },
            value: state.dateFieldState.displayValue,
            minValue: props.minValue,
            maxValue: props.maxValue,
            isDateUnavailable: props.isDateUnavailable,
            timeZone: state.timeZone,
        },
        timeInputProps: {
            value: state.timeValue,
            placeholderValue: getDateTimeValue(state.dateFieldState.displayValue),
            onUpdate: state.setTimeValue,
            format: state.timeFormat,
            readOnly: state.readOnly,
            disabled: state.disabled,
            timeZone: state.timeZone,
            hasClear: props.hasClear,
            size: props.size,
        },
    };
}
